import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter, useParams } from 'react-router-dom';
import { updateUserGroup } from 'js/actions/user-group-actions';
import {
  list as listFavoriteUserGroups,
  create as createFavoriteUserGroup,
  del as deleteFavoriteUserGroup,
} from 'js/actions/favorite-user-group-actions';
import { listUserGroupStats } from 'js/actions/user-group-stats-actions';
import UserGroupsDashboardComponent from '../components/UserGroupsDashboard/UserGroupsDashboardComponent';
import { selectActiveWorkspace } from 'js/store/reducers/workspaces';
import { useNotify } from 'js/hooks/useNotify';
import { useQuery } from 'js/hooks/useQuery';
import { SCHEMAS } from 'js/api/schemas';

const favoriteUserGroupsQueryId = (workspaceId) =>
  `favorite-user-groups-${workspaceId}`;

const userGroupStatsQueryId = (workspaceId) =>
  `user-group-stats-${workspaceId}`;

function UserGroupsDashboard({
  updateUserGroup,
  listFavoriteUserGroups,
  createFavoriteUserGroup,
  deleteFavoriteUserGroup,
  listUserGroupStats,
  workspace,
}) {
  const { workspaceId } = useParams();
  const notify = useNotify();

  const refreshFavoriteUserGroupsQuery = useCallback(
    (queryId) => listFavoriteUserGroups(workspaceId, queryId),
    [workspaceId, listFavoriteUserGroups]
  );

  const { refresh: refreshFavoriteUserGroups, result: favoriteUserGroups } =
    useQuery({
      queryId: favoriteUserGroupsQueryId(workspaceId),
      query: refreshFavoriteUserGroupsQuery,
      schema: SCHEMAS.FAVORITE_USER_GROUP_ARRAY,
    });

  const favoriteByUserGroupId = useMemo(() => {
    const items = favoriteUserGroups ?? [];
    return items.reduce((acc, item) => {
      if (item?.user_group?.id) {
        acc[item.user_group.id] = { is_favorited: !!item.is_favorited };
      }
      return acc;
    }, {});
  }, [favoriteUserGroups]);

  const refreshUserGroupStatsQuery = useCallback(
    (queryId) => listUserGroupStats(workspaceId, queryId),
    [workspaceId, listUserGroupStats]
  );

  const {
    hasLoaded: userGroupStatsHaveLoaded,
    result: userGroupStats,
    isLoading: userGroupStatsIsLoading,
    refresh: refreshUserGroupStats,
  } = useQuery({
    queryId: userGroupStatsQueryId(workspaceId),
    query: refreshUserGroupStatsQuery,
    schema: SCHEMAS.USER_GROUP_STAT_ARRAY,
  });

  const userGroups = useMemo(() => userGroupStats ?? [], [userGroupStats]);

  const groupsQuery = useMemo(
    () => ({
      isLoading: userGroupStatsIsLoading,
      finishedAt: userGroupStatsHaveLoaded ? Date.now() : 0,
    }),
    [userGroupStatsIsLoading, userGroupStatsHaveLoaded]
  );

  useEffect(() => {
    refreshUserGroupStats();
  }, [refreshUserGroupStats]);

  useEffect(() => {
    refreshFavoriteUserGroups();
  }, [refreshFavoriteUserGroups]);

  const modifyUserGroupState = useMemo(
    () => (newState) => (userGroup) => {
      updateUserGroup(userGroup.id, {
        archived_state: newState,
        archived_at: newState === 'archived' ? new Date() : null,
      })
        .then(() => {
          const op = newState === 'archived' ? 'archived' : 'restored';
          notify({
            type: 'success',
            message: `The "${userGroup.name}" group was successfully ${op}.`,
          });
        })
        .catch(() => {
          const op = newState === 'archived' ? 'archiving' : 'restoring';
          notify({
            type: 'danger',
            message: `Sorry, there was a problem ${op} the group, "${userGroup.name}".`,
          });
        });
    },
    [updateUserGroup, notify]
  );

  const handleArchiveUserGroup = useMemo(
    () => modifyUserGroupState('archived'),
    [modifyUserGroupState]
  );

  const handleRestoreUserGroup = useMemo(
    () => modifyUserGroupState(null),
    [modifyUserGroupState]
  );

  const handleToggleFavorite = useCallback(
    async (userGroupId, isFavorited) => {
      try {
        if (isFavorited) {
          await deleteFavoriteUserGroup(workspaceId, userGroupId);
        } else {
          await createFavoriteUserGroup(workspaceId, userGroupId);
        }
        refreshFavoriteUserGroups();
      } catch {
        notify({
          type: 'danger',
          message: 'Could not update favorite. Please try again.',
        });
      }
    },
    [
      workspaceId,
      deleteFavoriteUserGroup,
      createFavoriteUserGroup,
      refreshFavoriteUserGroups,
      notify,
    ]
  );

  return (
    <UserGroupsDashboardComponent
      userGroups={userGroups}
      groupsQuery={groupsQuery}
      workspace={workspace}
      favoriteByUserGroupId={favoriteByUserGroupId}
      onArchiveUserGroup={handleArchiveUserGroup}
      onRestoreUserGroup={handleRestoreUserGroup}
      onToggleFavorite={handleToggleFavorite}
    />
  );
}

export default withRouter(
  connect(
    (state) => {
      const workspace = selectActiveWorkspace(state);
      return {
        workspace,
      };
    },
    {
      updateUserGroup,
      listFavoriteUserGroups,
      createFavoriteUserGroup,
      deleteFavoriteUserGroup,
      listUserGroupStats,
    }
  )(UserGroupsDashboard)
);
